import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Spin, message } from 'antd';
import APIUtils from '../../api/APIUtils';
import WebLinks from '../../api/WebLinks';
import AnalysisSummary from './AnalysisSummary';
import AnalysisMasterSummary from './AnalysisMasterSummary';

const getHtmlContent = (data) => {
	if (!data) return '';
	if (typeof data !== 'string') {
		return '';
	}
	return data
		.trim()
		.replace(/^```(?:html)?\s*/i, '')
		.replace(/\s*```$/i, '')
		.trim();
};

const getParsedAnalysis = (data) => {
	if (!data) return null;
	if (typeof data === 'object') return data;
	if (typeof data !== 'string') return null;

	try {
		return JSON.parse(
			data
				.replace(/^```json\s*/i, '')
				.replace(/\s*```$/i, '')
				.trim()
		);
	} catch (_error) {
		return null;
	}
};

const ViewReport = () => {
	const { reportId } = useParams();
	const masterBuyerId = sessionStorage.getItem('isMasterBuyer');
	const isMasterBuyer =
		masterBuyerId && masterBuyerId !== 'undefined' && masterBuyerId !== 'null';
	const [reportContent, setReportContent] = useState('');
	const [loading, setLoading] = useState(false);
	const iframeRef = useRef(null);

	const handleIframeLoad = () => {
		const iframe = iframeRef.current;

		if (!iframe) return;

		const doc = iframe.contentDocument;

		if (!doc) return;

		const updateHeight = () => {
			const body = doc.body;
			const html = doc.documentElement;

			if (!body || !html) return;

			const height = Math.max(
				body.scrollHeight,
				body.offsetHeight,
				html.scrollHeight,
				html.offsetHeight
			);

			iframe.style.height = `${height}px`;
		};

		// First measurement
		updateHeight();

		// Wait for browser layout/repaint
		requestAnimationFrame(() => {
			updateHeight();

			requestAnimationFrame(() => {
				updateHeight();
			});
		});

		// Handle delayed resources/layout
		setTimeout(updateHeight, 100);
		setTimeout(updateHeight, 300);
		setTimeout(updateHeight, 600);
		setTimeout(updateHeight, 1000);

		// Handle images that haven't finished loading
		Array.from(doc.images).forEach((image) => {
			if (!image.complete) {
				image.addEventListener("load", updateHeight, { once: true });
				image.addEventListener("error", updateHeight, { once: true });
			}
		});
	};

	const fetchReport = async () => {
		if (!reportId) {
			setReportContent('');
			message.info('Analysis report not available');
			return;
		}
		setLoading(true);
		try {
			const response = await APIUtils.apiCall('GET', `${WebLinks.GetAnalysisReportHistoryDetails}${reportId}`);
			const detail = response.resultObj?.data?.analysisDetails ?? '';
			const cleanedDetail = getHtmlContent(detail);
			if (!response.isCatch && cleanedDetail) {
				setReportContent(detail);
			} else {
				setReportContent('');
				if (response.resultObj?.message) {
					message.error(response.resultObj.message || 'Analysis report not available');
				}
			}
		} catch (error) {
			setReportContent('');
			message.error('Unable to load analysis report');
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		fetchReport();
		return () => {
			if (iframeRef.current?._reportResizeObserver) {
				iframeRef.current._reportResizeObserver.disconnect();
				iframeRef.current._reportResizeObserver = null;
			}
		};
	}, [reportId]);

	const parsedReport = getParsedAnalysis(reportContent);
	const hasParsedReport = Boolean(parsedReport);
	const hasReportContent = Boolean(getHtmlContent(reportContent));

	return (
		<div className="p-4 bg-white rounded-lg shadow-sm min-h-[80vh] flex items-center justify-center">
			{loading ? (
				<div className="flex items-center justify-center w-full h-[calc(100vh-170px)]">
					<Spin />
				</div>
			) : hasParsedReport ? (
				<div className="w-full">
					{isMasterBuyer ? (
						<AnalysisMasterSummary report={parsedReport} />
					) : (
						<AnalysisSummary report={parsedReport} />
					)}
				</div>
			) : hasReportContent ? (
				<div className="w-full">
					<iframe
						ref={iframeRef}
						title="Analysis Report Preview"
						srcDoc={getHtmlContent(reportContent)}
						className="w-full border-0"
						style={{
							width: '100%',
							height: '1px',
							display: 'block',
							overflow: 'hidden',
						}}
						onLoad={handleIframeLoad}
					/>
				</div>
			) : (
				<div className="flex items-center justify-center h-[calc(100vh-170px)] text-slate-500">
					Analysis report not available
				</div>
			)}
		</div>
	);
};

export default ViewReport;
